/** @module Auth0Domain */
/* eslint-disable import/extensions */
import Auth0Tenant from './auth0-tenant-utils.mjs';
import {
  resolveTenantFromCustomDomain,
  getTenantClassFromCustomDomain,
  getProdCustomDomainPrefixes,
} from '../config/auth0-tenant-registry.mjs';

/**
 * Auth0 Domain utility class.
 *
 * Provides methods to work with custom signin domains and resolve tenants.
 * All logic delegates to auth0-tenant-registry.mjs for centralized configuration.
 */
class Auth0Domain {
  constructor(domain) {
    this._domain = domain;
  }

  /**
   * Derives the Auth0 domain (e.g., https://shipstation-local-z2.us.auth0.com) from the current
   * custom domain (e.g., signin-local-z2.shipstation.com or partners-signin-local-z2.shipstation.com).
   * This allows code to be tenant-class agnostic by deriving the Auth0 domain at runtime.
   * @returns {string} The Auth0 domain URL with protocol (e.g., https://shipstation-local-z2.us.auth0.com)
   */
  static getAuth0DomainFromCurrentLocation = () => {
    if (typeof window === 'undefined') {
      throw new Error('getAuth0DomainFromCurrentLocation can only be called in a browser environment');
    }
    const customDomain = window.location.hostname;
    const domain = new Auth0Domain(customDomain);
    const tenantName = domain.resolveTenant();
    const tenant = new Auth0Tenant(tenantName);
    return tenant.auth0BasedDomain; // includes https://
  };

  get domain() {
    return this._domain;
  }

  get url() {
    return this._domain;
  }

  /**
   * Check if a candidate string looks like a valid signin domain.
   * Uses the registry's custom domain prefixes to detect valid domains.
   * @param {string} candidate - The domain to check
   * @returns {boolean}
   */
  static isDomain(candidate) {
    if (!candidate) return false;
    // Check if domain contains any known custom domain prefix
    const prefixes = getProdCustomDomainPrefixes();
    return prefixes.some(prefix => candidate.includes(prefix));
  }

  /**
   * Get the tenant class for this domain.
   * @returns {string|null} The tenant class (e.g., 'shipstation', 'shipstation-partners') or null if not recognized
   */
  get tenantClass() {
    return getTenantClassFromCustomDomain(this._domain);
  }

  /**
   * Check if this domain belongs to a specific tenant class.
   * @param {string} tenantClass - The tenant class to check (e.g., 'shipstation', 'shipstation-partners')
   * @returns {boolean}
   */
  isInTenantClass(tenantClass) {
    return this.tenantClass === tenantClass;
  }

  /**
   * Resolve the tenant name from this domain.
   * Uses the registry to handle all tenant classes.
   * @returns {string} The tenant name (e.g., 'shipstation-local-z2')
   */
  resolveTenant = () => {
    const tenantName = resolveTenantFromCustomDomain(this._domain);
    if (tenantName) {
      return tenantName;
    }

    // Fallback for unrecognized domains (shouldn't happen in practice)
    return 'shipstation';
  };
}

export default Auth0Domain;
